import React, { useEffect, useState } from 'react';
import Footer from '../../../general/Footer';
import { Stack, Alert, Typography, Box } from '@mui/material';
import { Button } from 'components/index';
import { Delete } from '@mui/icons-material';
import WhiteFab from '../../../common/WhiteFab';
import { toast } from 'react-toastify';
import { useInvoiceInProgress } from '../context/InvoiceProgressContext';
import { Analytics, ANALYTICS_EVENTS } from '../../../../services/analyticsService';
import { useMonitoringReportShared } from '../context/MonitoringReportContext';
import { usePrompt } from '../../../AppPrompt';
import { exportAllInvoicesAsPDF } from '../../../invoice/invoice.util';
import { ReportInvoiceDTO } from '../types';
import ExportQualityDialog from './ExportQualityDialog';
import { PdfImageQuality, TARGET_SIZE_BYTES, formatBytes } from '../utils/pdfCompression';

interface ReportFooterProps {
  activeStep: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onDelete: () => void;
  stepCompletionStatus?: Array<{ isComplete: boolean; hasWarning: boolean; warningMessage?: string }>;
  stepTitles?: string[];
  invoiceStepIndex?: number; // Index of the invoice step (defaults to 3 for Limited Report)
  customButton?: React.ReactNode; // Custom button to render in the footer
}

const ReportFooter: React.FC<ReportFooterProps> = ({
  activeStep,
  totalSteps,
  onNext,
  onBack,
  onDelete,
  stepCompletionStatus = [],
  stepTitles = [],
  invoiceStepIndex = 3, // Default to 3 for Limited Report backward compatibility
  customButton,
}) => {
  const isFinalStep = activeStep === totalSteps - 1;
  const { isDirty, hasUnsavedPhotoChanges, saveReport, isSaving, downloadPDFAtQuality, reportState } = useMonitoringReportShared() as any;
  const { invoiceInProgress } = useInvoiceInProgress();
  const { getConfirmation } = usePrompt();
  const [lastSaveDisplay, setLastSaveDisplay] = useState<string>('');
  const [qualityDialogOpen, setQualityDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Get lastSavedAt from context if available (for auto-save indicator)
  const lastSavedAt = (reportState as any)?.lastSavedAt;

  // Update last save display every second
  useEffect(() => {
    const updateDisplay = () => {
      if (!lastSavedAt) {
        setLastSaveDisplay('');
        return;
      }

      const now = new Date();
      const diff = Math.floor((now.getTime() - lastSavedAt.getTime()) / 1000);

      if (diff < 60) {
        setLastSaveDisplay('Saved just now');
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60);
        setLastSaveDisplay(`Saved ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`);
      } else {
        const hours = Math.floor(diff / 3600);
        setLastSaveDisplay(`Saved ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`);
      }
    };

    updateDisplay();
    const interval = setInterval(updateDisplay, 1000);

    return () => clearInterval(interval);
  }, [lastSavedAt]);

  const handleDisabledClick = () => {
    if (invoiceInProgress) {
      toast.warning('Invoice in progress. Save or cancel invoice to proceed.');
    } else if (isSaving) {
      toast.warning('Saving in progress. Please wait...');
    }
  };

  const hasAnyUnsavedChanges = isDirty || hasUnsavedPhotoChanges;
  const hasAnyChanges = hasAnyUnsavedChanges;
  const isPDFReady = reportState.pdfPreview?.blobUrl && !reportState.pdfPreview?.isGenerating;
  const isExportEnabled = !!isPDFReady;

  const handleExport = async () => {
    if (!isFinalStep) return;
    if (isPDFReady) {
      // Check if invoice step has a warning about no current invoice
      const invoiceStepStatus = stepCompletionStatus[invoiceStepIndex];
      const hasNoCurrentInvoice = invoiceStepStatus?.hasWarning && invoiceStepStatus?.warningMessage?.includes('current invoice');

      // Check for current invoice existence
      const currentInvoice = reportState.invoices?.find((inv: ReportInvoiceDTO) => inv.currentInvoice);

      // If there's no current invoice, show a prompt
      if (hasNoCurrentInvoice || !currentInvoice) {
        const isConfirmed = await getConfirmation({
          heading: 'No Current Invoice',
          description: 'There is no current invoice for this report. Do you want to export the report only?',
          confirmButton: { label: 'Export Report Only', color: 'primary' },
          cancelButton: { label: 'Cancel', color: 'secondary' },
        });
        if (!isConfirmed) return;
      } else {
        // Check for other warnings (excluding invoice step if it has a warning)
        const stepsWithWarnings = stepCompletionStatus
          .map((status, index) => ({ status, index }))
          .filter(({ status, index }) => status.hasWarning && index !== invoiceStepIndex);

        if (stepsWithWarnings.length > 0) {
          const warningSteps = stepsWithWarnings
            .map(({ status, index }) => {
              const stepTitle = stepTitles[index] || `Step ${index + 1}`;
              return `<li><b>${stepTitle}:</b> ${status.warningMessage || 'Has warnings'}</li>`;
            })
            .join('');
          const isConfirmed = await getConfirmation({
            heading: 'Export with Warnings',
            description: `The following steps have warnings that may affect your report:
                <ul>${warningSteps}</ul>
                Do you want to continue with the export?`,
            confirmButton: { label: 'Export Anyway', color: 'error' },
            cancelButton: { label: 'Go Back', color: 'secondary' },
          });
          if (!isConfirmed) return;
        }
      }

      // Warnings are cleared; let the user pick a compression level before we render.
      setQualityDialogOpen(true);
    } else {
      toast.error('PDF preview not ready. Please wait for generation to complete.');
    }
  };

  const handleConfirmExport = async (quality: PdfImageQuality) => {
    const currentInvoice = reportState.invoices?.find((inv: ReportInvoiceDTO) => inv.currentInvoice);

    setIsExporting(true);
    try {
      Analytics.track(ANALYTICS_EVENTS.EXPORT_REPORT, {
        project_id: String(reportState.projectSummary?.id ?? ''),
        project_label: `${reportState.projectSummary?.projectNumber ?? ''} - ${reportState.projectSummary?.projectTitle ?? ''}`.trim(),
        report_type: reportState.reportType,
        image_quality: quality,
      });

      const deliveredBytes = await downloadPDFAtQuality(quality);
      setQualityDialogOpen(false);
      // The report download is done; only the (optional) invoice export is
      // left, which is a separate concern the dialog's loading state shouldn't
      // keep reflecting now that the dialog itself is gone.
      setIsExporting(false);

      // The estimate shown in the dialog can be off; tell the user what they
      // actually got, folded into one message rather than a second toast, and
      // always appended at the end so the wording stays consistent.
      const sizeNote = deliveredBytes ? ` (${formatBytes(deliveredBytes)})` : '';
      const sizeWarning =
        deliveredBytes && deliveredBytes > TARGET_SIZE_BYTES ? ' — above the 10 MB target, try a smaller quality setting' : '';

      // Export current invoice if it exists
      if (currentInvoice && reportState.projectSummary) {
        try {
          const projectData = {
            projectNumber: reportState.projectSummary.projectNumber,
            projectTitle: reportState.projectSummary.projectTitle,
            clientName: reportState.projectSummary.client?.name,
            clientAddress: reportState.projectSummary.client?.address,
            projectAddress: reportState.projectSummary.projectAddress,
          };

          await exportAllInvoicesAsPDF(reportState.invoices ?? [], projectData, reportState.projectSummary.projectNumber, true);

          // Show combined success message
          toast.success(`Report and invoice exported successfully!${sizeNote}${sizeWarning}`);
        } catch (error) {
          console.error('Error exporting invoice:', error);
          // Report was exported but invoice failed
          toast.success(`Report exported successfully!${sizeNote}${sizeWarning}`);
          toast.error('Failed to export invoice');
        }
      } else {
        // Only report was exported
        toast.success(`Report exported successfully!${sizeNote}${sizeWarning}`);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      // Leave the dialog open so the user can retry or pick another quality.
      toast.error(error instanceof Error ? error.message : 'Failed to export report');
    } finally {
      // Still needed for the error path above, where the dialog stays open.
      setIsExporting(false);
    }
  };

  return (
    <Footer>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1} spacing={2}>
        {/* Left side: Delete button + Save status */}
        <Stack direction={'row'} alignItems={'center'} spacing={2}>
          <WhiteFab
            textColor='error'
            onClick={invoiceInProgress || isSaving ? handleDisabledClick : onDelete}
            icon={<Delete fontSize='small' />}
            size='small'
            disabled={invoiceInProgress || isSaving}
          />

          {/* Save status indicator */}
          {!invoiceInProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isSaving ? (
                <Typography variant='body2' sx={{ color: 'primary.main', fontWeight: 500 }}>
                  Saving...
                </Typography>
              ) : hasAnyChanges ? (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Unsaved changes
                </Typography>
              ) : lastSaveDisplay ? (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {lastSaveDisplay}
                </Typography>
              ) : null}
            </Box>
          )}
        </Stack>

        {/* Center: Invoice alert */}
        {invoiceInProgress && (
          <Alert
            severity='warning'
            sx={{
              py: 0.5,
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiAlert-message': { fontWeight: 500 },
            }}
          >
            Save or cancel invoice to proceed
          </Alert>
        )}

        {/* Right side: Action buttons */}
        <Stack direction={'row'} spacing={2}>
          {customButton}
          <Button
            variant='outlined'
            onClick={invoiceInProgress || isSaving ? handleDisabledClick : saveReport}
            disabled={!hasAnyUnsavedChanges || invoiceInProgress || isSaving}
            loading={isSaving}
          >
            Save Draft
          </Button>
          <Button
            variant='outlined'
            onClick={invoiceInProgress || isSaving ? handleDisabledClick : onBack}
            disabled={activeStep === 0 || invoiceInProgress || isSaving}
          >
            Back
          </Button>
          {isFinalStep ? (
            <Button
              variant='contained'
              onClick={invoiceInProgress || isSaving ? handleDisabledClick : handleExport}
              disabled={invoiceInProgress || isSaving || !isExportEnabled}
            >
              Export
            </Button>
          ) : (
            <Button
              variant='contained'
              onClick={invoiceInProgress || isSaving ? handleDisabledClick : onNext}
              disabled={invoiceInProgress || isSaving}
            >
              Next
            </Button>
          )}
        </Stack>
      </Stack>

      <ExportQualityDialog
        open={qualityDialogOpen}
        baseline={reportState.pdfPreview?.sizeInfo}
        isExporting={isExporting}
        onCancel={() => setQualityDialogOpen(false)}
        onConfirm={handleConfirmExport}
      />
    </Footer>
  );
};

export default ReportFooter;
